import { useQuery } from '@tanstack/react-query'
import { buildApiUrl } from '../config'
import { useAppStore } from '../stores/appStore'

interface BedVariation {
  id: number
  title: string
  slug: string
  bed: {
    id: number
    title: string
    collection?: {
      id: number
      title: string
    } | null
  }
  bed_type?: {
    id: number
    title: string
  } | null
  height?: number
  in_byob: boolean
  featured_image?: string
  product_thumbnail?: string
}

interface BedVariationsResponse {
  count: number
  limit: number
  offset: number
  results: BedVariation[]
}

export default function BedList() {
  const { market, lang, setMarket } = useAppStore()

  const { data, isLoading, error } = useQuery<BedVariationsResponse>({
    queryKey: ['bed-variations', market, lang],
    queryFn: () => {
      const params = new URLSearchParams({
        market: market,
        lang: lang,
        in_byob: 'true', // Only show beds available in BYOB
        limit: '200' // Get all beds
      })
      return fetch(buildApiUrl(`bed-variations/?${params.toString()}`))
        .then(res => res.json())
    },
    enabled: !!market && !!lang,
    staleTime: 0, // Always consider data stale
    cacheTime: 0, // Don't cache at all
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window gains focus
  })

  const handleBedSelect = (bedSlug: string) => {
    // Navigate to the bed editor with the selected bed
    const params = new URLSearchParams({
      market: market,
      lang: lang,
      bed: bedSlug
    })
    window.location.href = `/?${params.toString()}`
  }

  const handleMarketChange = (newMarket: string) => {
    setMarket(newMarket)
    // Update URL
    const params = new URLSearchParams({
      market: newMarket,
      lang: lang
    })
    window.history.replaceState({}, '', `/bed-list?${params.toString()}`)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600">Could not load the bed list. Please try again later.</p>
        </div>
      </div>
    )
  }

  const beds = data?.results || []

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1>Beds ({beds.length})</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm">Market:</label>
          <select
            value={market}
            onChange={(e) => handleMarketChange(e.target.value)}
            className="px-2 py-1 border rounded text-sm"
          >
            <option value="se">Sweden (SE)</option>
            <option value="no">Norway (NO)</option>
            <option value="dk">Denmark (DK)</option>
            <option value="fi">Finland (FI)</option>
            <option value="de">Germany (DE)</option>
            <option value="at">Austria (AT)</option>
            <option value="ch">Switzerland (CH)</option>
            <option value="nl">Netherlands (NL)</option>
            <option value="be">Belgium (BE)</option>
            <option value="uk">United Kingdom (UK)</option>
            <option value="ie">Ireland (IE)</option>
            <option value="fr">France (FR)</option>
            <option value="es">Spain (ES)</option>
            <option value="it">Italy (IT)</option>
            <option value="pt">Portugal (PT)</option>
            <option value="pl">Poland (PL)</option>
            <option value="cz">Czech Republic (CZ)</option>
            <option value="hr">Croatia (HR)</option>
            <option value="ee">Estonia (EE)</option>
            <option value="is">Iceland (IS)</option>
            <option value="tr">Turkey (TR)</option>
            <option value="ae">Dubai (AE)</option>
            <option value="in">India (IN)</option>
            <option value="my">Malaysia (MY)</option>
            <option value="mt">Malta (MT)</option>
            <option value="ko">South Korea (KO)</option>
            <option value="int">International (INT)</option>
            <option value="cnt">Contract (CNT)</option>
            <option value="r3">R3 (R3)</option>
          </select>
        </div>
      </div>
      
      {beds.map((bed) => (
        <div key={bed.id} className="py-2 border-b">
          <button
            onClick={() => handleBedSelect(bed.slug)}
            className="text-left hover:underline"
          >
            {bed.title}
          </button>
        </div>
      ))}
      
      <div className="mt-4">
        <button
          onClick={() => {
            const params = new URLSearchParams({ market, lang })
            window.location.href = `/?${params.toString()}`
          }}
        >
          ← Back
        </button>
      </div>
    </div>
  )
}
